<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref} from 'vue';
import {ElDialog, ElSkeleton} from 'element-plus';
import {KpiGraph, KpiGraphUnresolvedReason} from 'common';
import {kpiGraph} from '@/base/callBackend';
import type {Core} from 'cytoscape';

const REASON_TEXT: Record<KpiGraphUnresolvedReason, string> = {
    [KpiGraphUnresolvedReason.nietGevonden]: 'geen KPI met deze naam of korte naam',
    [KpiGraphUnresolvedReason.nietUniek]   : 'meerdere KPI\'s met deze naam of korte naam',
    [KpiGraphUnresolvedReason.mijlpaal]    : 'verwijst naar een mijlpaal',
    [KpiGraphUnresolvedReason.dynamisch]   : 'dynamische verwijzing, de KPI-naam is pas bij evaluatie bekend',
};

const visible   = defineModel<boolean>({required: true});
const loading   = ref(true);
const graph     = ref<KpiGraph>();
const container = ref<HTMLElement>();

let cy: Core | undefined;

const hasEdges       = computed(() => (graph.value?.edges.length ?? 0) > 0);
const cyclesAsNames  = computed(() => (graph.value?.cycles ?? []).map(cycle => [...cycle, cycle[0]].map(nameOf).join(' → ')));
const unresolvedRows = computed(() => (graph.value?.unresolved ?? []).map(u => ({
    kpiName: nameOf(u.from),
    veld   : u.field === 'waardeExpression' ? 'waarde' : 'gehaald',
    ref    : u.ref ?? '-',
    reden  : REASON_TEXT[u.reason],
})));

onBeforeUnmount(destroyGraph);

function nameOf(uuid: string): string {
    return graph.value?.nodes.find(n => n.uuid === uuid)?.name ?? uuid;
}

async function onOpen() {
    loading.value = true;
    graph.value   = await kpiGraph();
    loading.value = false;
    await nextTick();
    if (hasEdges.value) {
        await renderGraph();
    }
}

function destroyGraph() {
    cy?.destroy();
    cy = undefined;
}

async function renderGraph() {
    const g = graph.value!;
    const [{default: cytoscape}, {default: dagre}] = await Promise.all([import('cytoscape'), import('cytoscape-dagre')]);
    cytoscape.use(dagre);

    const cycleNodes = new Set<string>(g.cycles.flat());
    const cycleEdges = new Set<string>();
    for (const cycle of g.cycles) {
        for (let i = 0; i < cycle.length; i++) {
            cycleEdges.add(`${cycle[i]}->${cycle[(i + 1) % cycle.length]}`);
        }
    }
    // only kpis that are part of a dependency are drawn, the rest would just clutter the graph
    const connected = new Set<string>(g.edges.flatMap(e => [e.from, e.to]));

    destroyGraph();
    cy = cytoscape({
        container: container.value,
        elements : [
            ...g.nodes
                .filter(n => connected.has(n.uuid))
                .map(n => ({
                    data   : {
                        id   : n.uuid,
                        label: n.korteNaam ? `${n.name}\n(${n.korteNaam})` : n.name,
                    },
                    classes: cycleNodes.has(n.uuid) ? 'cycle' : '',
                })),
            ...g.edges.map(e => ({
                data   : {
                    id    : `${e.from}->${e.to}:${e.field}`,
                    source: e.from,
                    target: e.to,
                },
                classes: `${e.field === 'gehaaldExpression' ? 'gehaald' : 'waarde'}${cycleEdges.has(`${e.from}->${e.to}`) ? ' cycle' : ''}`,
            })),
        ],
        layout   : {
            name   : 'dagre',
            rankDir: 'LR',
            nodeSep: 30,
            rankSep: 80,
        } as any,
        style    : [
            {
                selector: 'node',
                style   : {
                    'label'           : 'data(label)',
                    'text-wrap'       : 'wrap',
                    'text-max-width'  : '160px',
                    'text-valign'     : 'center',
                    'text-halign'     : 'center',
                    'font-size'       : '11px',
                    'shape'           : 'round-rectangle',
                    'width'           : '180px',
                    'height'          : '50px',
                    'background-color': '#e5e7eb',
                    'border-width'    : 1,
                    'border-color'    : '#6b7280',
                },
            },
            {
                selector: 'edge',
                style   : {
                    'curve-style'       : 'bezier',
                    'width'             : 1.5,
                    'line-color'        : '#6b7280',
                    'target-arrow-shape': 'triangle',
                    'target-arrow-color': '#6b7280',
                },
            },
            {
                selector: 'edge.gehaald',
                style   : {'line-style': 'dashed'},
            },
            {
                selector: 'node.cycle',
                style   : {
                    'background-color': '#fecaca',
                    'border-color'    : '#dc2626',
                    'border-width'    : 2,
                },
            },
            {
                selector: 'edge.cycle',
                style   : {
                    'line-color'        : '#dc2626',
                    'target-arrow-color': '#dc2626',
                },
            },
        ],
    });
}
</script>

<template>
    <el-dialog
        v-model="visible"
        width="90%"
        align-center
        destroy-on-close
        @open="onOpen"
        @closed="destroyGraph"
    >
        <template #header>
            <div class="text-lg font-semibold my-0.5 mx-3">
                Afhankelijkheden tussen KPI's
            </div>
        </template>

        <div v-if="loading">
            <el-skeleton :rows="5" animated/>
        </div>
        <template v-else>
            <div v-if="cyclesAsNames.length"
                 class="border border-red-600 bg-red-100 text-red-800 p-2 mb-2">
                <div class="font-semibold">Circulaire afhankelijkheden gevonden:</div>
                <div v-for="(cycle, i) in cyclesAsNames" :key="i">{{ cycle }}</div>
            </div>
            <template v-if="hasEdges">
                <div ref="container" class="h-[65vh] border border-gray-400 bg-white"></div>
                <div class="text-sm text-gray-600 mt-1">
                    A → B: een expressie van A verwijst naar B (doorgetrokken: waarde-expressie, gestreept: gehaald-expressie)
                </div>
            </template>
            <div v-else class="text-gray-600">
                Geen afhankelijkheden tussen KPI's gevonden.
            </div>
            <div v-if="unresolvedRows.length" class="mt-4">
                <div class="text-lg mb-1">Niet-herleidbare verwijzingen</div>
                <table class="text-sm">
                    <thead>
                        <tr class="text-left">
                            <th class="p-1 px-3 bg-white">KPI</th>
                            <th class="p-1 px-3 bg-white">expressie</th>
                            <th class="p-1 px-3 bg-white">verwijzing</th>
                            <th class="p-1 px-3 bg-white">reden</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in unresolvedRows" :key="i">
                            <td class="p-1 px-3 bg-gray-200">{{ row.kpiName }}</td>
                            <td class="p-1 px-3 bg-gray-200">{{ row.veld }}</td>
                            <td class="p-1 px-3 bg-gray-200">{{ row.ref }}</td>
                            <td class="p-1 px-3 bg-gray-200">{{ row.reden }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </el-dialog>
</template>
